import React, {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import debounce from "lodash.debounce";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Button } from "../button";
import { ExportBlacklistingReportForExcelPDFBody } from "../../hooks/report/interface";
import { useGetBlacklistingReport } from "../../hooks/report/query";
import { EXPORT_PAGE_SIZE } from "../../constants/common";

interface SearchBlacklistingReportCardProps {
  setAllBlacklisting: Dispatch<SetStateAction<never[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  setCount: Dispatch<SetStateAction<number>>;
  setExportBody: Dispatch<
    SetStateAction<ExportBlacklistingReportForExcelPDFBody | undefined>
  >;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const SearchBlacklistingReportCardSchema = Yup.object({
  eid: Yup.string(),
  passportNumber: Yup.string(),
  iqama: Yup.string(),
  visaNumber: Yup.string(),
});

const initialState = {
  eid: "",
  passportNumber: "",
  iqama: "",
  visaNumber: "",
};

const SearchBlacklistingReportCard = ({
  setAllBlacklisting,
  page,
  setPages,
  setCount,
  setExportBody,
  pageSize,
  setPage,
}: SearchBlacklistingReportCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    reset,
  } = useFormWithSchema(SearchBlacklistingReportCardSchema);

  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  // const { data: allNationalities } = useGetAllNationalities();

  // const nationalities = useMemo(
  //   () => mapToOptions("name", "name_Ar", "iso3", allNationalities),
  //   [allNationalities]
  // );

  const eid = watch("eid");
  const passportNumber = watch("passportNumber");
  const iqama = watch("iqama");
  const visaNumber = watch("visaNumber");

  const [debouncedEID, setDebouncedEID] = useState(eid);

  const handleEIDChange = useCallback(
    debounce((value) => setDebouncedEID(value), 500),
    []
  );

  const [debouncedPassportNumber, setDebouncedPassportNumber] =
    useState(passportNumber);

  const handlePassportChange = useCallback(
    debounce((value) => setDebouncedPassportNumber(value), 500),
    []
  );

  const [debouncedIqama, setDebouncedIqama] = useState(iqama);

  const handleIqamaChange = useCallback(
    debounce((value) => setDebouncedIqama(value), 500),
    []
  );

  const [debouncedVisaNumber, setDebouncedVisaNumber] = useState(visaNumber);

  const handleVisaNumberChange = useCallback(
    debounce((value) => setDebouncedVisaNumber(value), 500),
    []
  );

  useEffect(() => {
    setPage(1);
  }, [eid, passportNumber, visaNumber, iqama, setPage]);

  useEffect(() => {
    handleEIDChange(eid);
    return () => handleEIDChange.cancel();
  }, [eid, handleEIDChange]);

  useEffect(() => {
    handlePassportChange(passportNumber);
    return () => handlePassportChange.cancel();
  }, [passportNumber, handlePassportChange]);

  useEffect(() => {
    handleIqamaChange(iqama);
    return () => handleIqamaChange.cancel();
  }, [iqama, handleIqamaChange]);

  useEffect(() => {
    handleVisaNumberChange(visaNumber);
    return () => handleVisaNumberChange.cancel();
  }, [visaNumber, handleVisaNumberChange]);

  const { data: blacklistingReport } = useGetBlacklistingReport({
    S_EmiratesID: debouncedEID,
    S_PassportNumber: debouncedPassportNumber,
    S_Iqama: debouncedIqama,
    S_VisaNo: debouncedVisaNumber,
    pageNumber: page,
    pageSize: pageSize,
    search: "",
  });

  useEffect(() => {
    if (blacklistingReport) {
      setAllBlacklisting(blacklistingReport.data || []);
      setPages(blacklistingReport.pagination?.pages || 1);
      setCount(blacklistingReport.pagination?.count || 0);
    }
  }, [setAllBlacklisting, setPages, setCount, blacklistingReport]);

  useEffect(() => {
    setExportBody({
      S_EmiratesID: debouncedEID,
      S_PassportNumber: debouncedPassportNumber,
      S_Iqama: debouncedIqama,
      S_VisaNo: debouncedVisaNumber,
      pageNumber: page,
      pageSize: EXPORT_PAGE_SIZE,
      search: "",
    });
  }, [
    setExportBody,
    debouncedEID,
    debouncedPassportNumber,
    debouncedIqama,
    debouncedVisaNumber,
    page,
  ]);

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2 lg:gap-4">
              <Controller
                name="eid"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("nationalID")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                  />
                )}
              />
              <Controller
                name="passportNumber"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("passportNumber")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                  />
                )}
              />

              <Controller
                name="iqama"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("iqama")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                  />
                )}
              />

              <Controller
                name="visaNumber"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("visaNumber")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    value={value || ""}
                  />
                )}
              />
            </div>
          </form>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default memo(SearchBlacklistingReportCard);
